// src/pages/ShopifyPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  Zap,
  DollarSign,
  Clock,
  ShoppingCart,
  Mail,
  BarChart2,
  MessageSquare,
  Star,
  TrendingUp,
  Package,
} from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../hooks/useLanguage";

// --- static meta (icons + colors kept out of CONTENT) -----------------------

const PAIN_ICONS = [ShoppingCart, Mail, BarChart2, MessageSquare, Package, TrendingUp];
const PAIN_COLORS = [
  { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
  { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
  { color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  { color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  { color: "text-[#3b82f6]", bg: "bg-blue-500/10", border: "border-blue-500/20" },
];
const TOOL_META = [
  { score: 94, color: "bg-blue-500" },
  { score: 87, color: "bg-purple-500" },
  { score: 91, color: "bg-blue-500" },
  { score: 83, color: "bg-orange-500" },
];

// --- CONTENT ------------------------------------------------------------------

const CONTENT = {
  en: {
    title: "AI Blueprint for Shopify Stores — Know Which AI Tools Will Pay Off",
    metaDesc: "Get a personalized AI implementation blueprint for your Shopify store. Exactly which tools to use, in what order, with real ROI projections. Built from 50K+ ecommerce store data.",
    heroBadge: "Built for Shopify store owners",
    heroTitlePart1: "Which AI Tools Will",
    heroTitlePart2: "Actually Grow Your Store?",
    heroSubtitlePre: "Answer 8 questions about your store. Get a personalized blueprint with the",
    heroSubtitleBold: "exact tools, setup order, and ROI projections",
    heroSubtitlePost: "— built from",
    heroSubtitleEnd: "vetted AI tools and 2026 ecommerce industry benchmarks.",
    heroBullets: [
      "Recover abandoned carts — AI flows recover 18—27% on average",
      "Know which ad spend is actually profitable (to the dollar)",
      "Automate 68% of customer support tickets with AI chat",
      "3x your email revenue per send with behavioral triggers",
    ],
    heroCta: "Recover My Abandoned Carts",
    heroCtaFine: "One-time — No subscription — Ready in minutes",
    painEyebrow: "Sound familiar?",
    painHeading: "The 6 Revenue Leaks in Most Shopify Stores",
    painSub: "AI can fix all of these — but only if you know which tools to use and in what order. That's what the blueprint tells you.",
    painPoints: [
      { title: "Cart abandonment eating your revenue", desc: "70%+ of carts are abandoned. Most stores recover less than 5% of them. AI-powered flows recover 18—27%.", stat: "$250—$400/day lost" },
      { title: "Email campaigns that underperform", desc: "Generic blasts get 12% open rates. AI-segmented flows average 38—45% open rates and 3x the revenue per send.", stat: "3x revenue per send" },
      { title: "No clear view of what's actually driving sales", desc: "You're spending on ads but can't tell which campaigns, channels, or products are actually profitable.", stat: "22% ad spend wasted avg" },
      { title: "Customer support is a manual grind", desc: "Hours answering the same order questions. AI chat handles 68% of Shopify support tickets automatically, 24/7.", stat: "68% tickets auto-handled" },
      { title: "Product recommendations aren't converting", desc: "Shoppers who click a recommendation spend 12x more. Most stores show the same products to everyone.", stat: "12x higher spend" },
      { title: "One-time buyers instead of loyal customers", desc: "Industry average: 27% repeat purchase rate. AI-powered post-purchase flows push this to 42—56%.", stat: "56% repeat rate possible" },
    ],
    toolEyebrow: "Tool stack preview",
    toolHeading: "Tools Your Blueprint Could Recommend",
    toolSubPre: "These are examples. Your blueprint picks from",
    toolSubPost: "+ tools based on your store's specific problems, size, and budget.",
    toolStack: [
      { name: "Klaviyo AI", category: "Email & SMS", price: "From $45/mo", why: "Behavioral triggers + AI send-time optimization. Average 3x ROI on email for Shopify stores." },
      { name: "Tidio", category: "Live Chat & AI Support", price: "Free — $49/mo", why: "Handles 68% of Shopify support tickets automatically. Free tier covers most small stores." },
      { name: "Triple Whale", category: "Analytics & Attribution", price: "From $129/mo", why: "True attribution across Meta, Google, TikTok. Know exactly which ads are profitable." },
      { name: "Nosto", category: "Personalization", price: "Revenue share", why: "AI product recommendations that lift AOV 22—35%. Used by 2,000+ Shopify stores." },
    ],
    matchLabel: "match",
    toolFootnotePre: "Your blueprint selects 5—8 tools from a database of",
    toolFootnotePost: "+, ranked for your specific store.",
    getEyebrow: "What you get",
    getHeadingPre: "Your $",
    getHeadingPost: " Shopify AI Blueprint Includes",
    whatYouGet: [
      { title: "Your exact Shopify AI tool stack", desc: "5—8 tools matched to your store size, budget, and specific problems. With real pricing and setup steps." },
      { title: "12-week implementation roadmap", desc: "Week-by-week plan. Phase 1 starts this week — no waiting for the perfect moment." },
      { title: "Custom prompt library for Shopify", desc: "15—30 prompts for product copy, email campaigns, ad hooks, and customer responses. Ready to paste." },
      { title: "ROI projections for your store", desc: "Expected cart recovery, email revenue lift, and hours saved — built from 2026 ecommerce benchmarks and vetted AI tool data." },
      { title: "Risk guide and gotcha warnings", desc: "What commonly goes wrong with each tool and how to avoid it. Built from real implementation patterns." },
      { title: "First 30 days checklist", desc: "Day-by-day tasks for your first month. See results in cart recovery, open rates, or support hours saved." },
    ],
    testimonialLaunch: "We launched recently — real customer results coming soon.",
    testimonialGuarantee: "60-day money-back guarantee if it doesn't deliver value.",
    bottomHeading1: "Stop Guessing.",
    bottomHeading2: "Know Exactly What to Build Next.",
    bottomSub: "Answer 8 questions about your Shopify store. Get a blueprint with your exact tool stack, setup order, and week-by-week roadmap. Ready in minutes.",
    bottomCta: "Get My Shopify AI Blueprint",
    bottomFineReady: "Ready in minutes",
    bottomFineOneTime: "One-time $",
    bottomFineNoSub: "No subscription",
    preview: {
      eyebrow: "AI Implementation Blueprint",
      kicker: "Shopify Store — E-commerce",
      reportReady: "✓ Report Ready",
      roiLabel: "Year 1 ROI",
      hoursSavedLabel: "Saved/week",
      toolsLabel: "Recommended",
      hrsUnit: "hrs",
      toolsUnit: "tools",
      topToolsEyebrow: "Top Tools For Your Store",
      toolBadges: ["Email — $45/mo", "Support — Free", "Analytics — $129/mo"],
      matchShort: "match",
      roadmapEyebrow: "12-Week Roadmap",
      phaseLabels: ["Phase 1\nCart Recovery", "Phase 2\nEmail & Analytics", "Phase 3\nScale"],
      floatingBadge1: "✨ Built in 3 min",
      floatingBadge2: "Shopify-optimized",
    },
    faqEyebrow: "Frequently asked",
    faqHeading: "Before you build your blueprint",
    faq: [
      { q: "I run a fashion DTC / print-on-demand / subscription / B2B Shopify store. Does this still apply?", a: "Yes. The blueprint asks about your store format, GMV range, and biggest bottleneck, then tailors the stack. Fashion DTC usually gets email and CRO AI; POD and subscription get LTV and retention tools; B2B Shopify gets quote, account-pricing, and wholesale-portal tools." },
      { q: "How long does the blueprint take to implement?", a: "It's a 12-week roadmap built for operators putting in 2—4 hours a week. Phase 1 targets your biggest revenue leak first — usually cart recovery or email segmentation — and most stores see measurable results in 2—3 weeks before Phase 2." },
      { q: "I've installed AI Shopify apps before and they didn't move the needle. Why is this different?", a: "Most stores install an app before defining the metric it should move, never set up the integrations with Klaviyo / GA4 / ad platforms, and never compare LTV or AOV against baseline. The blueprint ranks apps by fit-score to your store, maps integrations, and projects ROI on revenue per visitor before you install anything." },
      { q: "Is my customer and order data safe?", a: "Every tool recommendation includes a data-handling note — where customer data is stored, whether it's used to train models, and PCI posture for anything touching payments. Apps that ask for read/write on orders and customers with no clear data contract are flagged, not quietly recommended." },
      { q: "What if the blueprint doesn't fit my store?", a: "60-day money-back guarantee, no questions asked. Email us within 60 days and we refund in full. You keep the blueprint. We'd rather you leave happy than keep $149 on a report you couldn't use." },
    ],
  },
  es: {
    title: "Blueprint de IA para Tiendas Shopify — Sabe Qué Herramientas de IA Van a Rendir",
    metaDesc: "Recibe un blueprint personalizado de implementación de IA para tu tienda Shopify. Exactamente qué herramientas usar, en qué orden, con proyecciones reales de ROI. Creado con datos de más de 50.000 tiendas de ecommerce.",
    heroBadge: "Diseñado para dueños de tiendas Shopify",
    heroTitlePart1: "¿Qué Herramientas de IA Van a",
    heroTitlePart2: "Hacer Crecer Tu Tienda de Verdad?",
    heroSubtitlePre: "Responde 8 preguntas sobre tu tienda. Obtén un blueprint personalizado con las",
    heroSubtitleBold: "herramientas exactas, el orden de implementación y las proyecciones de ROI",
    heroSubtitlePost: "— construido a partir de",
    heroSubtitleEnd: "herramientas de IA validadas y benchmarks del sector ecommerce 2026.",
    heroBullets: [
      "Recupera carritos abandonados — los flujos de IA recuperan 18—27% en promedio",
      "Sabe exactamente qué gasto en ads es rentable (al dólar)",
      "Automatiza el 68% de los tickets de soporte con chat de IA",
      "Triplica tus ingresos por envío de email con disparadores por comportamiento",
    ],
    heroCta: "Recupera Mis Carritos Abandonados",
    heroCtaFine: "Pago único — Sin suscripción — Listo en minutos",
    painEyebrow: "¿Te suena familiar?",
    painHeading: "Las 6 Fugas de Ingresos en la Mayoría de Tiendas Shopify",
    painSub: "La IA puede resolver todo esto — pero solo si sabes qué herramientas usar y en qué orden. Eso es lo que te dice el blueprint.",
    painPoints: [
      { title: "El abandono de carrito se come tus ingresos", desc: "Más del 70% de los carritos se abandonan. La mayoría de tiendas recupera menos del 5%. Los flujos con IA recuperan entre 18—27%.", stat: "$250—$400/día perdidos" },
      { title: "Campañas de email que rinden poco", desc: "Los envíos genéricos tienen 12% de apertura. Los flujos segmentados con IA promedian 38—45% de apertura y 3x los ingresos por envío.", stat: "3x ingresos por envío" },
      { title: "Sin visibilidad clara de qué está impulsando las ventas", desc: "Gastas en ads pero no sabes qué campañas, canales o productos son realmente rentables.", stat: "22% del gasto en ads desperdiciado" },
      { title: "El soporte al cliente es puro trabajo manual", desc: "Horas respondiendo las mismas preguntas de pedidos. El chat con IA resuelve el 68% de los tickets de Shopify automáticamente, 24/7.", stat: "68% de tickets automatizados" },
      { title: "Las recomendaciones de producto no convierten", desc: "Los compradores que hacen clic en una recomendación gastan 12x más. La mayoría de tiendas muestra los mismos productos a todos.", stat: "12x más gasto" },
      { title: "Compradores de una sola vez en lugar de clientes leales", desc: "Promedio del sector: 27% de recompra. Los flujos post-compra con IA suben esto a 42—56%.", stat: "56% de recompra posible" },
    ],
    toolEyebrow: "Vista previa del stack",
    toolHeading: "Herramientas Que Tu Blueprint Podría Recomendar",
    toolSubPre: "Son ejemplos. Tu blueprint elige entre",
    toolSubPost: "+ herramientas según los problemas específicos, tamaño y presupuesto de tu tienda.",
    toolStack: [
      { name: "Klaviyo AI", category: "Email y SMS", price: "Desde $45/mes", why: "Disparadores por comportamiento + optimización con IA del horario de envío. ROI promedio de 3x en email para tiendas Shopify." },
      { name: "Tidio", category: "Chat en Vivo y Soporte IA", price: "Gratis — $49/mes", why: "Resuelve el 68% de los tickets de Shopify automáticamente. El plan gratuito cubre a la mayoría de tiendas pequeñas." },
      { name: "Triple Whale", category: "Analítica y Atribución", price: "Desde $129/mes", why: "Atribución real en Meta, Google y TikTok. Sabe exactamente qué anuncios son rentables." },
      { name: "Nosto", category: "Personalización", price: "Revenue share", why: "Recomendaciones de producto con IA que elevan el AOV 22—35%. Usado por más de 2.000 tiendas Shopify." },
    ],
    matchLabel: "compatibilidad",
    toolFootnotePre: "Tu blueprint selecciona 5—8 herramientas de una base de datos de",
    toolFootnotePost: "+, clasificadas para tu tienda específica.",
    getEyebrow: "Lo que obtienes",
    getHeadingPre: "Tu Blueprint de IA Shopify por $",
    getHeadingPost: " Incluye",
    whatYouGet: [
      { title: "Tu stack exacto de herramientas de IA para Shopify", desc: "5—8 herramientas elegidas según el tamaño de tu tienda, presupuesto y problemas específicos. Con precios reales y pasos de implementación." },
      { title: "Hoja de ruta de 90 días", desc: "Plan semana a semana. La Fase 1 arranca esta semana — no hay que esperar el momento perfecto." },
      { title: "Biblioteca de prompts para Shopify", desc: "15—30 prompts para copy de producto, campañas de email, ganchos publicitarios y respuestas a clientes. Listos para pegar." },
      { title: "Proyecciones de ROI para tu tienda", desc: "Recuperación esperada de carritos, aumento de ingresos por email y horas ahorradas — basado en benchmarks de ecommerce 2026 y datos validados de herramientas de IA." },
      { title: "Guía de riesgos y advertencias", desc: "Qué suele salir mal con cada herramienta y cómo evitarlo. Construida a partir de patrones reales de implementación." },
      { title: "Checklist de los primeros 30 días", desc: "Tareas día a día para tu primer mes. Ve resultados en recuperación de carritos, tasas de apertura u horas ahorradas en soporte." },
    ],
    testimonialLaunch: "Acabamos de lanzar — resultados reales de clientes muy pronto.",
    testimonialGuarantee: "Garantía de reembolso de 60 días si no aporta valor.",
    bottomHeading1: "Deja de Adivinar.",
    bottomHeading2: "Sabe Exactamente Qué Construir Después.",
    bottomSub: "Responde 8 preguntas sobre tu tienda Shopify. Obtén un blueprint con tu stack exacto, orden de implementación y hoja de ruta semana a semana. Listo en minutos.",
    bottomCta: "Arma Mi Blueprint Shopify",
    bottomFineReady: "Listo en minutos",
    bottomFineOneTime: "Pago único $",
    bottomFineNoSub: "Sin suscripción",
    preview: {
      eyebrow: "Blueprint de Implementación de IA",
      kicker: "Tienda Shopify — E-commerce",
      reportReady: "✓ Informe Listo",
      roiLabel: "ROI Año 1",
      hoursSavedLabel: "Ahorro/semana",
      toolsLabel: "Recomendadas",
      hrsUnit: "hrs",
      toolsUnit: "herramientas",
      topToolsEyebrow: "Top Herramientas Para Tu Tienda",
      toolBadges: ["Email — $45/mes", "Soporte — Gratis", "Analítica — $129/mes"],
      matchShort: "match",
      roadmapEyebrow: "Hoja de Ruta 12 Semanas",
      phaseLabels: ["Fase 1\nRecuperación de Carrito", "Fase 2\nEmail y Analítica", "Fase 3\nEscala"],
      floatingBadge1: "✨ Listo en 3 min",
      floatingBadge2: "Optimizado para Shopify",
    },
    faqEyebrow: "Preguntas frecuentes",
    faqHeading: "Antes de armar tu blueprint",
    faq: [
      { q: "Tengo una tienda Shopify de moda DTC, print-on-demand, suscripción o B2B. ¿Igual aplica?", a: "Sí. El blueprint te pregunta tu formato, rango de GMV y mayor cuello de botella, y arma el stack a tu medida. Moda DTC suele recibir IA de email y CRO; POD y suscripción reciben herramientas de LTV y retención; Shopify B2B recibe cotizaciones, precios por cuenta y portal de mayoristas." },
      { q: "¿Cuánto tarda implementar el blueprint?", a: "Es una hoja de ruta de 12 semanas pensada para operadores con 2—4 horas a la semana. La Fase 1 ataca tu mayor fuga primero — normalmente recuperación de carrito o segmentación de email — y la mayoría ve resultados medibles en 2—3 semanas antes de pasar a la Fase 2." },
      { q: "Ya instalé apps de IA de Shopify y no movieron la aguja. ¿Por qué esto es distinto?", a: "La mayoría instala una app antes de definir la métrica que debe mover, nunca configura las integraciones con Klaviyo / GA4 / plataformas de ads y nunca compara LTV o AOV contra el baseline. El blueprint rankea apps por ajuste a tu tienda, mapea integraciones y proyecta el ROI en ingreso por visitante antes de que instales nada." },
      { q: "¿Están seguros los datos de mis clientes y pedidos?", a: "Cada recomendación incluye una nota de manejo de datos — dónde se almacenan, si se usan para entrenar modelos y la postura PCI para lo que toca pagos. Las apps que piden read/write de pedidos y clientes sin contrato de datos claro se marcan, no se recomiendan en silencio." },
      { q: "¿Y si el blueprint no encaja con mi tienda?", a: "Garantía de devolución de 60 días, sin preguntas. Nos escribes dentro de los 60 días y te devolvemos todo. Te quedas con el blueprint. Preferimos que te vayas contento antes que cobrarte $149 por un reporte que no pudiste usar." },
    ],
  },
  tr: {
    title: "Shopify Mağazaları için Yapay Zeka Planı — Hangi Yapay Zeka Araçlarının Getiri Sağlayacağını Öğrenin",
    metaDesc: "Shopify mağazanız için kişiselleştirilmiş bir yapay zeka uygulama planı alın. Tam olarak hangi araçları, hangi sırayla kullanacağınızı gerçek ROI projeksiyonlarıyla birlikte görün. 50.000+ e-ticaret mağaza verisiyle hazırlandı.",
    heroBadge: "Shopify mağaza sahipleri için tasarlandı",
    heroTitlePart1: "Hangi Yapay Zeka Araçları",
    heroTitlePart2: "Mağazanızı Gerçekten Büyütür?",
    heroSubtitlePre: "Mağazanız hakkında 8 soruyu yanıtlayın.",
    heroSubtitleBold: "tam araç listesi, kurulum sırası ve ROI projeksiyonları",
    heroSubtitlePost: "içeren kişiselleştirilmiş bir plan edinin —",
    heroSubtitleEnd: "onaylı yapay zeka aracı ve 2026 e-ticaret kıyaslamalarıyla hazırlandı.",
    heroBullets: [
      "Terk edilmiş sepetleri kurtarın — yapay zeka akışları ortalama %18—27 kurtarır",
      "Hangi reklam harcamasının gerçekten kârlı olduğunu dolarına kadar öğrenin",
      "Yapay zeka sohbetle müşteri destek taleplerinin %68'ini otomatikleştirin",
      "Davranışsal tetikleyicilerle e-posta başına geliri 3 katına çıkarın",
    ],
    heroCta: "Terk Edilen Sepetleri Kurtarın",
    heroCtaFine: "Tek seferlik — Abonelik yok — Dakikalar içinde hazır",
    painEyebrow: "Tanıdık geliyor mu?",
    painHeading: "Çoğu Shopify Mağazasındaki 6 Gelir Sızıntısı",
    painSub: "Yapay zeka bunların hepsini çözebilir — ama yalnızca hangi araçları hangi sırayla kullanacağınızı bilirseniz. Plan size tam olarak bunu söyler.",
    painPoints: [
      { title: "Sepet terki gelirinizi yiyor", desc: "Sepetlerin %70'inden fazlası terk ediliyor. Çoğu mağaza bunların %5'inden azını kurtarabiliyor. Yapay zeka destekli akışlar %18—27'sini kurtarır.", stat: "Günde $250—$400 kayıp" },
      { title: "İyi performans göstermeyen e-posta kampanyaları", desc: "Genel toplu gönderimler %12 açılma oranı alır. Yapay zeka ile segmentlenmiş akışlar ortalama %38—45 açılma ve gönderim başına 3 kat gelir sağlar.", stat: "Gönderim başına 3x gelir" },
      { title: "Satışları neyin gerçekten sürüklediğine dair netlik yok", desc: "Reklama harcama yapıyorsunuz ama hangi kampanyaların, kanalların veya ürünlerin gerçekten kârlı olduğunu söyleyemiyorsunuz.", stat: "Ortalama %22 reklam harcaması israf" },
      { title: "Müşteri desteği manuel bir angarya", desc: "Aynı sipariş sorularını yanıtlamak saatler alıyor. Yapay zeka sohbet, Shopify destek taleplerinin %68'ini otomatik olarak, 7/24 yönetir.", stat: "%68 talep otomatik çözülüyor" },
      { title: "Ürün önerileri dönüşüm sağlamıyor", desc: "Bir öneriye tıklayan alışverişçiler 12 kat daha fazla harcar. Çoğu mağaza herkese aynı ürünleri gösterir.", stat: "12 kat yüksek harcama" },
      { title: "Sadık müşteri yerine tek seferlik alıcılar", desc: "Sektör ortalaması: %27 tekrar alışveriş oranı. Yapay zeka destekli satış sonrası akışlar bunu %42—56'ya çıkarır.", stat: "%56 tekrar oranı mümkün" },
    ],
    toolEyebrow: "Araç önizlemesi",
    toolHeading: "Planınızın Önerebileceği Araçlar",
    toolSubPre: "Bunlar örnek. Planınız, mağazanızın sorunlarına, büyüklüğüne ve bütçenize göre",
    toolSubPost: "+ araç arasından seçim yapar.",
    toolStack: [
      { name: "Klaviyo AI", category: "E-posta ve SMS", price: "Aylık $45'ten başlar", why: "Davranışsal tetikleyiciler + yapay zeka gönderim zamanı optimizasyonu. Shopify mağazaları için e-postada ortalama 3x ROI." },
      { name: "Tidio", category: "Canlı Sohbet ve Yapay Zeka Destek", price: "Ücretsiz — $49/ay", why: "Shopify destek taleplerinin %68'ini otomatik olarak yönetir. Ücretsiz paket küçük mağazaların çoğunu kapsar." },
      { name: "Triple Whale", category: "Analitik ve Atıf", price: "Aylık $129'dan başlar", why: "Meta, Google, TikTok genelinde gerçek atıf. Hangi reklamların kârlı olduğunu tam olarak bilin." },
      { name: "Nosto", category: "Kişiselleştirme", price: "Gelir paylaşımı", why: "AOV'yi %22—35 artıran yapay zeka ürün önerileri. 2.000'den fazla Shopify mağazası kullanıyor." },
    ],
    matchLabel: "uyum",
    toolFootnotePre: "Planınız, mağazanıza göre sıralanmış",
    toolFootnotePost: "+ araçlık veri tabanından 5—8 araç seçer.",
    getEyebrow: "Neler alıyorsunuz",
    getHeadingPre: "$",
    getHeadingPost: "'lık Shopify Yapay Zeka Planınız Şunları İçerir",
    whatYouGet: [
      { title: "Tam Shopify yapay zeka araç listeniz", desc: "Mağaza büyüklüğünüze, bütçenize ve sorunlarınıza göre seçilmiş 5—8 araç. Gerçek fiyatlar ve kurulum adımlarıyla birlikte." },
      { title: "90 günlük uygulama yol haritası", desc: "Hafta hafta plan. Faz 1 bu hafta başlar — mükemmel anı beklemeye gerek yok." },
      { title: "Shopify'a özel prompt kütüphanesi", desc: "Ürün metni, e-posta kampanyaları, reklam cümleleri ve müşteri yanıtları için 15—30 prompt. Kopyala-yapıştır hazır." },
      { title: "Mağazanız için ROI projeksiyonları", desc: "Beklenen sepet kurtarma, e-posta geliri artışı ve kazanılan saatler — 2026 e-ticaret kıyaslamalarına ve onaylı yapay zeka aracı verisine dayanır." },
      { title: "Risk rehberi ve tuzak uyarıları", desc: "Her araçta yaygın olarak ne ters gider ve nasıl kaçınılır. Gerçek uygulama örüntülerinden derlendi." },
      { title: "İlk 30 gün kontrol listesi", desc: "İlk ayınız için gün gün görevler. Sepet kurtarma, açılma oranları veya destekte kazanılan saatlerde sonuç görün." },
    ],
    testimonialLaunch: "Yeni başladık — gerçek müşteri sonuçları yakında.",
    testimonialGuarantee: "Değer getirmezse 60 gün para iade garantisi.",
    bottomHeading1: "Tahmin Etmeyi Bırakın.",
    bottomHeading2: "Sırada Ne İnşa Edeceğinizi Tam Olarak Bilin.",
    bottomSub: "Shopify mağazanız hakkında 8 soruyu yanıtlayın. Tam araç listeniz, kurulum sıranız ve hafta hafta yol haritanızla planınızı alın. Dakikalar içinde hazır.",
    bottomCta: "Shopify AI Blueprint'imi Al",
    bottomFineReady: "Dakikalar içinde hazır",
    bottomFineOneTime: "Tek seferlik $",
    bottomFineNoSub: "Abonelik yok",
    preview: {
      eyebrow: "Yapay Zeka Uygulama Planı",
      kicker: "Shopify Mağazası — E-ticaret",
      reportReady: "✓ Rapor Hazır",
      roiLabel: "1. Yıl ROI",
      hoursSavedLabel: "Haftalık tasarruf",
      toolsLabel: "Önerilen",
      hrsUnit: "saat",
      toolsUnit: "araç",
      topToolsEyebrow: "Mağazanız İçin En İyi Araçlar",
      toolBadges: ["E-posta — $45/ay", "Destek — Ücretsiz", "Analitik — $129/ay"],
      matchShort: "uyum",
      roadmapEyebrow: "12 Haftalık Yol Haritası",
      phaseLabels: ["Faz 1\nSepet Kurtarma", "Faz 2\nE-posta ve Analitik", "Faz 3\nÖlçek"],
      floatingBadge1: "✨ 3 dakikada hazır",
      floatingBadge2: "Shopify odaklı",
    },
    faqEyebrow: "Sıkça sorulanlar",
    faqHeading: "Blueprint'inizi oluşturmadan önce",
    faq: [
      { q: "Moda DTC / print-on-demand / abonelik / B2B Shopify mağazası işletiyorum. Yine de işe yarar mı?", a: "Evet. Blueprint size mağaza formatınızı, GMV aralığınızı ve en büyük darboğazınızı sorar ve stack'i buna göre özelleştirir. Moda DTC genellikle e-posta ve CRO AI'ı alır; POD ve abonelik LTV ve elde tutma araçları alır; B2B Shopify teklif, hesap fiyatlandırma ve toptan portal araçları alır." },
      { q: "Blueprint'i uygulamak ne kadar sürer?", a: "Haftada 2—4 saat ayırabilen işletmeciler için tasarlanmış 12 haftalık bir yol haritasıdır. 1. Faz önce en büyük gelir kaybınıza — genellikle sepet kurtarma veya e-posta segmentasyonu — odaklanır ve çoğu mağaza 2. Faz'a geçmeden önce 2—3 hafta içinde ölçülebilir sonuç görür." },
      { q: "Daha önce Shopify AI uygulamaları kurdum ve iğneyi oynatmadı. Bunun farkı ne?", a: "Çoğu mağaza hareket ettirmesi gereken metriği tanımlamadan uygulama kurar, Klaviyo / GA4 / reklam platformları entegrasyonlarını hiç yapmaz ve LTV veya AOV'yi temel değerle karşılaştırmaz. Blueprint uygulamaları mağazanıza uygunluğa göre sıralar, entegrasyonları haritalandırır ve hiçbir şey kurmadan önce ziyaretçi başına gelir üzerinden ROI öngörür." },
      { q: "Müşteri ve sipariş verilerim güvende mi?", a: "Her öneride veri işleme notu vardır — verilerin nerede tutulduğu, model eğitiminde kullanılıp kullanılmadığı ve ödemelere dokunan şeyler için PCI duruşu. Net bir veri sözleşmesi olmadan sipariş ve müşteriler üzerinde read/write isteyen uygulamalar işaretlenir, sessizce önerilmez." },
      { q: "Blueprint mağazama uymazsa ne olur?", a: "60 gün içinde koşulsuz para iade garantisi. 60 gün içinde bize yazarsınız, tam iade yaparız. Blueprint sizde kalır. 149 dolarınızı tutup mutsuz göndermektense kullanamayacağınız bir rapor için iade etmeyi tercih ederiz." },
    ],
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

// --- Blueprint Preview (Shopify-specific) -------------------------------------
const ShopifyBlueprintPreview = ({ c }) => (
  <div className="relative select-none">
    <div className="absolute -inset-4 bg-gradient-to-br from-green-600/20 via-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl" />
    <div className="relative rounded-2xl overflow-hidden bg-slate-800/90 border border-white/10 shadow-2xl backdrop-blur-sm">
      {/* Top bar */}
      <div className="bg-slate-900 px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-[#3b82f6] font-semibold tracking-widest uppercase mb-0.5">{c.preview.eyebrow}</div>
          <div className="text-sm font-bold text-white">{c.preview.kicker}</div>
        </div>
        <span className="text-[10px] font-semibold bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-2.5 py-1 rounded-full">
          {c.preview.reportReady}
        </span>
      </div>

      {/* ROI bar */}
      <div className="grid grid-cols-3 border-b border-white/10">
        {[["$10–42K", c.preview.roiLabel], [`8 ${c.preview.hrsUnit}`, c.preview.hoursSavedLabel], [`5–8 ${c.preview.toolsUnit}`, c.preview.toolsLabel]].map(([val, lab]) => (
          <div key={lab} className="px-4 py-3 text-center border-r last:border-r-0 border-white/10">
            <div className="text-base font-bold text-[#3b82f6]">{val}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{lab}</div>
          </div>
        ))}
      </div>

      {/* Tool list */}
      <div className="p-4 space-y-2.5">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.topToolsEyebrow}</div>
        {[
          { name: "Klaviyo AI", badge: c.preview.toolBadges[0], score: 94, color: "bg-blue-500" },
          { name: "Tidio Chat", badge: c.preview.toolBadges[1], score: 87, color: "bg-purple-500" },
          { name: "Triple Whale", badge: c.preview.toolBadges[2], score: 91, color: "bg-blue-500" },
        ].map((tool) => (
          <div key={tool.name} className="bg-slate-900/80 rounded-xl p-3 border border-white/10/40">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-white">{tool.name}</span>
              <span className="text-[10px] text-slate-400">{tool.badge}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full ${tool.color} rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: `${tool.score}%` }}
                  transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
                />
              </div>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{tool.score}% {c.preview.matchShort}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Phase roadmap preview */}
      <div className="px-4 pb-4">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.roadmapEyebrow}</div>
        <div className="flex gap-2">
          {c.preview.phaseLabels.map((p, i) => (
            <div
              key={p}
              className={`flex-1 text-center rounded-lg py-2 px-1 text-[10px] font-medium border ${
                i === 0
                  ? "bg-green-500/20 border-green-500/40 text-green-400"
                  : "bg-white/8 border-white/10 text-slate-500"
              }`}
            >
              {p.split("\n").map((line, j) => <div key={j}>{line}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Floating badges */}
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -top-4 -right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-blue-500/40"
    >
      {c.preview.floatingBadge1}
    </motion.div>
    <motion.div
      animate={{ y: [0, 6, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      className="absolute -bottom-3 -left-4 bg-purple-600 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-purple-600/40"
    >
      {c.preview.floatingBadge2}
    </motion.div>
  </div>
);

// --- component ----------------------------------------------------------------
const ShopifyPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;
  const [toolCount, setToolCount] = useState(null);
  const displayCount = toolCount || 348;
  const [currentPrice, setCurrentPrice] = useState(149);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com';
    fetch(`${base}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
    fetch(`${base}/api/pricing/current`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.price) setCurrentPrice(d.price); })
      .catch(() => {});
  }, []);

  const goToAudit = () => {
    navigate("/audit?industry=retail&businessType=ecommerce&platform=shopify");
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <SEOHead
        path="/shopify"
        lang={lang}
        title={c.title}
        description={c.metaDesc}
        faqItems={c.faq || c.faqs}
        industry="E-commerce / Shopify"
      />
      <Header />

      {/* -- HERO ---------------------------------------------------------------- */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        {/* Background gradient mesh */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-green-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-purple-600/8 rounded-full filter blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* LEFT: Copy */}
            <div>
              {/* Shopify badge */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-semibold px-4 py-2 rounded-full mb-8"
              >
                <span className="text-base">🛒</span>
                {c.heroBadge}
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6"
              >
                <span className="text-white">{c.heroTitlePart1}</span>
                <br />
                <span className="bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {c.heroTitlePart2}
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl"
              >
                {c.heroSubtitlePre}{" "}
                <span className="text-white font-semibold">{c.heroSubtitleBold}</span>{" "}
                {c.heroSubtitlePost} {displayCount} {c.heroSubtitleEnd}
              </motion.p>

              {/* Bullets */}
              <motion.ul
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-3 mb-10"
              >
                {c.heroBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </motion.ul>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 items-start"
              >
                <Button
                  onClick={goToAudit}
                  size="lg"
                  className="bg-green-500 hover:bg-green-400 text-slate-950 font-black text-lg px-10 py-6 rounded-2xl shadow-lg shadow-green-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  {c.heroCta} · ${currentPrice}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-xs text-slate-500 self-center">
                  {c.heroCtaFine}
                </p>
              </motion.div>
            </div>

            {/* RIGHT: Blueprint mockup */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="hidden lg:block"
            >
              <ShopifyBlueprintPreview c={c} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* -- PAIN POINTS --------------------------------------------------------- */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-green-400 font-semibold tracking-widest uppercase mb-3">
                {c.painEyebrow}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                {c.painHeading}
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                {c.painSub}
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.painPoints.map((pp, idx) => {
                const Icon = PAIN_ICONS[idx];
                const cls = PAIN_COLORS[idx];
                return (
                  <motion.div
                    key={pp.title}
                    variants={fadeUp}
                    className={`rounded-2xl border p-6 ${cls.bg} ${cls.border}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls.bg} border ${cls.border} mb-4`}>
                      <Icon className={`w-5 h-5 ${cls.color}`} />
                    </div>
                    <h3 className="text-white font-bold text-base mb-2">{pp.title}</h3>
                    <p className="text-slate-400 text-sm mb-3 leading-relaxed">{pp.desc}</p>
                    <div className={`text-xs font-bold ${cls.color}`}>{pp.stat}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* -- TOOL PREVIEW ------------------------------------------------------- */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-blue-400 font-semibold tracking-widest uppercase mb-3">
                {c.toolEyebrow}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                {c.toolHeading}
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                {c.toolSubPre} {displayCount}{c.toolSubPost}
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-5">
              {c.toolStack.map((tool, idx) => {
                const meta = TOOL_META[idx];
                return (
                  <motion.div
                    key={tool.name}
                    variants={fadeUp}
                    className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-white font-bold text-lg">{tool.name}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{tool.category} — {tool.price}</div>
                      </div>
                      <div className="flex items-center gap-1.5 bg-slate-900/60 border border-white/10 rounded-full px-3 py-1">
                        <div className={`w-2 h-2 rounded-full ${meta.color}`} />
                        <span className="text-xs text-slate-400 font-semibold">{meta.score}% {c.matchLabel}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{tool.why}</p>
                    <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full ${meta.color} rounded-full`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${meta.score}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div variants={fadeUp} className="text-center mt-10">
              <p className="text-slate-500 text-sm">
                {c.toolFootnotePre} {displayCount}{c.toolFootnotePost}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* -- WHAT YOU GET ------------------------------------------------------- */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-purple-400 font-semibold tracking-widest uppercase mb-3">
                {c.getEyebrow}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                {c.getHeadingPre}{currentPrice}{c.getHeadingPost}
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-5">
              {c.whatYouGet.map((item, i) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  className="flex gap-4 p-5 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl"
                >
                  <div className="w-8 h-8 rounded-xl bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm mb-1">{item.title}</div>
                    <div className="text-slate-400 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS — awaiting real customer results */}
      <section className="py-16 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-slate-500 text-sm py-8">
            <span className="text-slate-600">{c.testimonialGuarantee}</span>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm text-[#3b82f6] font-semibold tracking-widest uppercase mb-3">{c.faqEyebrow}</div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">{c.faqHeading}</h2>
          </div>
          <div className="space-y-3">
            {c.faq.map((item, i) => (
              <details key={i} className="group bg-slate-900/70 border border-white/10 rounded-2xl px-5 py-4 transition-colors hover:border-blue-500/30">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="text-white font-semibold text-sm leading-snug">{item.q}</span>
                  <span className="text-slate-500 group-open:rotate-45 transition-transform text-xl flex-shrink-0">+</span>
                </summary>
                <p className="text-slate-400 text-sm leading-relaxed mt-3 pt-3 border-t border-white/8">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* -- BOTTOM CTA ---------------------------------------------------------- */}
      <section className="py-24 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-2xl bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-8">
              <Zap className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              {c.bottomHeading1}
              <br />
              <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                {c.bottomHeading2}
              </span>
            </h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">
              {c.bottomSub}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={goToAudit}
                size="lg"
                className="bg-green-500 hover:bg-green-400 text-slate-950 font-black text-xl px-12 py-7 rounded-2xl shadow-xl shadow-green-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                {c.bottomCta} · ${currentPrice}
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 mt-8">
              {[
                { icon: Clock, text: c.bottomFineReady },
                { icon: DollarSign, text: `${c.bottomFineOneTime}${currentPrice}` },
                { icon: CheckCircle, text: c.bottomFineNoSub },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-slate-500 text-sm">
                  <Icon className="w-4 h-4" />
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShopifyPage;
